import Modal from "./Modal";
import { BLOCK_ICON_MAP, GOOGLE_MIMETYPE_MAP, GOOGLE_TO_MICROSOFT_MIMETYPE_MAP, GOOGLE_TO_MICROSOFT_NAME_MAP, MIMETYPE_ICON_MAP } from "../utils";
import { Attachment, AttachmentType, Workflow } from "../interfaces";
import GooglePickerButton from "./GooglePickerButton";
import { CheckIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "../contexts";

interface Props {
  workflow: Workflow;
  attachments: Array<Attachment>;
  updateAttachments: (attachments: Array<Attachment>) => void;
  showMicrosoft?: boolean;
  postSigningStage?: boolean;
  onClose?: () => void;
}

function EmailEditorAttachmentsModal(props: Props) {
  const { t } = useTranslation();
  const { workflow, attachments, updateAttachments } = props;
  const { closeModal } = useModal();

  const [showPreview, setShowPreview] = useState(true);

  function findAttachment(attachmentType: string, workflowDocument: string): Attachment | undefined {
    return attachments.find((a: Attachment) => a.workflowDocument === workflowDocument && a.attachmentType === attachmentType);
  }

  function addAttachment(attachmentType: AttachmentType, payload: { file?: { id: string, name: string, mimeType: string }, workflowDocument?: string }) {
    let attachmentName = payload.file?.name ?? "";
    let attachmentMimeType = payload.file?.mimeType ?? "";

    if (payload.workflowDocument) {
      const document = workflow.documents.find(d => d.id === payload.workflowDocument);
      if (!document || !document.file)
        return;

      attachmentName = document.outputName;

      if (attachmentType === "ATTACH_OUTPUT_AS_PDF")
        attachmentMimeType = "application/pdf";

      if (attachmentType === "ATTACH_OUTPUT_AS_MS_FILE")
        attachmentMimeType = GOOGLE_TO_MICROSOFT_MIMETYPE_MAP[document.documentType];

      if (attachmentType === "INSERT_OUTPUT_AS_LINK")
        attachmentMimeType = GOOGLE_MIMETYPE_MAP[document.documentType];
    }

    const attachmentId = "temp-" + Math.random().toString(36).substr(2, 9);
    const attachment: Attachment = {
      id: attachmentId,
      attachmentType,
      attachmentName,
      attachmentMimeType,
      workflowDocument: payload.workflowDocument ?? null,
      sharingMode: "VIEWER",
      driveFile: payload.file ?? null
    }

    updateAttachments([...attachments, attachment]);
  }

  async function removeAttachment(attachmentId: string) {
    updateAttachments(attachments.filter(a => (a.id !== attachmentId)));
  }

  function toggleOutputAttachment(attachmentType: AttachmentType, workflowDocument: string) {
    const attachment = findAttachment(attachmentType, workflowDocument)
    if (attachment)
      removeAttachment(attachment.id);
    else
      addAttachment(attachmentType, { workflowDocument });

    closeModal(props.onClose);
  }

  return (
    <Modal title={t("Add Attachments")} onClose={props.onClose}>
      <div className="flex flex-col gap-4">
        <div className="overflow-y-auto flex flex-col gap-4 max-h-[300px] pr-6 -mr-6 w-full items-center">

          {workflow.documents.map((document) =>
            <div key={document.id} className="flex flex-col gap-1 w-full">
              <div className="font-gilroy font-semibold text-gray-600 text-sm truncate w-80">
                {document.file?.name}
              </div>
              <div className="flex gap-4">

                {!(document.removeOutput || document.documentType === "PDF_FILLABLE" || document.documentType.startsWith("MICROSOFT_")) &&
                  <div className={`flex flex-col gap-1 p-2 w-[188px] h-[160px] bg-gray-100 shadow rounded border border-gray-100 ${!findAttachment("INSERT_OUTPUT_AS_LINK", document.id) ? "cursor-pointer hover:border-blue" : "cursor-not-allowed hover:border-red"}`} onClick={() => toggleOutputAttachment("INSERT_OUTPUT_AS_LINK", document.id)}>
                    <div className="text-xs flex items-bottom truncate flex-shrink-0 gap-1 font-semibold">
                      <img className="w-3 h-3 flex-shrink-0" src={BLOCK_ICON_MAP[document.documentType]} alt="Document icon" />
                      {t("Share via Link")}
                      {findAttachment("INSERT_OUTPUT_AS_LINK", document.id) &&
                        <CheckIcon className="w-3 h-3 bg-green text-white rounded-full flex-shrink-0 p-px ml-auto" />
                      }
                    </div>
                    <div className="relative w-full h-40 bg-gray-200 border-gray-200 overflow-hidden rounded flex">
                      {(showPreview && document.file?.id)
                        ? <img className="relative w-full rounded shadow m-auto border border-gray-200" onError={() => setShowPreview(false)} src={`https://drive.google.com/thumbnail?authuser=0&sz=w320&id=${document.file!.id}`} alt="Thumbnail" />
                        : <img className="relative h-1/2 m-auto drop-shadow" src={BLOCK_ICON_MAP[document.documentType]} alt="Document icon" />
                      }
                    </div>
                  </div>
                }

                <div className={`flex flex-col gap-1 p-2 w-[188px] h-[160px] bg-gray-100 shadow rounded border border-gray-100 ${!findAttachment("ATTACH_OUTPUT_AS_PDF", document.id) ? "cursor-pointer hover:border-blue" : "cursor-not-allowed hover:border-red"}`} onClick={() => toggleOutputAttachment("ATTACH_OUTPUT_AS_PDF", document.id)}>
                  <div className="text-xs flex items-bottom truncate flex-shrink-0 gap-1 font-semibold">
                    <img className="w-3 h-3 flex-shrink-0" src={MIMETYPE_ICON_MAP["application/pdf"]} alt="PDF icon" />
                    {t("Attach")} {(document.isSignable && props.postSigningStage) ? t("Signed") : t("as")} {t("PDF")}
                    {findAttachment("ATTACH_OUTPUT_AS_PDF", document.id) &&
                      <CheckIcon className="w-3 h-3 bg-green text-white rounded-full flex-shrink-0 p-px ml-auto" />
                    }
                  </div>
                  <div className="relative w-full h-40 bg-gray-200 border-gray-200 overflow-hidden rounded flex">
                    {(showPreview && document.file?.id)
                      ? <img className="relative w-full rounded shadow m-auto border border-gray-200" onError={() => setShowPreview(false)} src={`https://drive.google.com/thumbnail?authuser=0&sz=w320&id=${document.file!.id}`} alt="Thumbnail" />
                      : <img className="relative h-1/2 m-auto drop-shadow" src={BLOCK_ICON_MAP["PDF"]} alt="PDF icon" />
                    }
                  </div>
                </div>

                {(props.showMicrosoft && document.documentType !== "PDF_FILLABLE") &&
                  <div className={`flex flex-col gap-1 p-2 w-[188px] h-[160px] bg-gray-100 shadow rounded border border-gray-100 ${!findAttachment("ATTACH_OUTPUT_AS_MS_FILE", document.id) ? "cursor-pointer hover:border-blue" : "cursor-not-allowed hover:border-red"}`} onClick={() => toggleOutputAttachment("ATTACH_OUTPUT_AS_MS_FILE", document.id)}>
                    <div className="text-xs flex items-bottom truncate flex-shrink-0 gap-1 font-semibold">
                      <img className="w-3 h-3 flex-shrink-0" src={MIMETYPE_ICON_MAP[GOOGLE_TO_MICROSOFT_MIMETYPE_MAP[document.documentType]]} alt="Microsoft File Icon" />
                      {t("Attach as")} {GOOGLE_TO_MICROSOFT_NAME_MAP[document.documentType].replace("Microsoft", "MS")}
                      {findAttachment("ATTACH_OUTPUT_AS_MS_FILE", document.id) &&
                        <CheckIcon className="w-3 h-3 bg-green text-white rounded-full flex-shrink-0 p-px ml-auto" />
                      }
                    </div>
                    <div className="relative w-full h-40 bg-gray-200 border-gray-200 overflow-hidden rounded flex">
                      {(showPreview && document.file?.id)
                        ? <img className="relative w-full rounded shadow m-auto border border-gray-200" onError={() => setShowPreview(false)} src={`https://drive.google.com/thumbnail?authuser=0&sz=w320&id=${document.file!.id}`} alt="Thumbnail" />
                        : <img className="relative h-1/2 m-auto drop-shadow" src={MIMETYPE_ICON_MAP[GOOGLE_TO_MICROSOFT_MIMETYPE_MAP[document.documentType]]} alt="Document icon" />
                      }
                    </div>
                  </div>
                }
              </div>

            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="h-px bg-gray-400 w-full" />
          <div className="flex-shrink-0">or</div>
          <div className="h-px bg-gray-400 w-full" />
        </div>

        <GooglePickerButton onPicked={(file: any) => { addAttachment("ATTACH_DRIVE_FILE", { file }); closeModal(); }} blockType="ALL" displayUploadTab>
          <div className="flex items-center gap-2 font-gilroy font-semibold text-gray-600 hover:underline hover:text-blue">
            <img src="https://static.portant.co/drive-icon.svg" className="w-5 h-5" alt="Drive icon" />
            {t("Select from Google Drive")}
          </div>
        </GooglePickerButton>
      </div>
    </Modal>
  )
}

export default EmailEditorAttachmentsModal;
